import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AppProvider } from './context/AuthContext';
import { MergeGroupProvider } from './context/MergeGroupContext';
import { AuthGuard } from './components/auth/AuthGuard';
import { Layout } from './components/layout/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ContentManagement from './pages/Content';
import Reservations from './pages/Reservations';
import Reviews from './pages/Reviews';
import Settings from './pages/Settings';
import BlogManagement from './pages/BlogManagement';
import NotFound from './pages/NotFound';
import ZohoCallback from './pages/ZohoCallback';
import Unauthorized from './pages/Unauthorized';
import VerifyOTP from './pages/Auth/VerifyOTP';
import ResetPassword from './pages/Auth/ResetPassword';

// Toast configuration
const toastConfig = {
  duration: 4000,
  position: 'top-right' as const,
  style: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
    color: '#1f2937',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    borderRadius: '12px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    fontSize: '14px',
    fontWeight: '500',
    padding: '12px 16px',
  },
  success: {
    duration: 3000,
    style: {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      color: '#166534',
      border: '1px solid #bbf7d0',
    },
    iconTheme: {
      primary: '#10b981',
      secondary: '#fff',
    },
  },
  error: {
    duration: 5000,
    style: {
      background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
      color: '#991b1b',
      border: '1px solid #fecaca',
    },
    iconTheme: {
      primary: '#ef4444',
      secondary: '#fff',
    },
  },
};

function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <MergeGroupProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <div className="App">
              <Toaster {...toastConfig} />
          
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/zoho/callback" element={<ZohoCallback />} />
            <Route path="/verify-otp" element={<VerifyOTP />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Protected routes */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={
              <AuthGuard>
                <Layout>
                  <Dashboard />
                </Layout>
              </AuthGuard>
            } />
            
            {/* Content Management */}
            <Route path="/content" element={
              <AuthGuard>
                <Layout>
                  <ContentManagement />
                </Layout>
               </AuthGuard>
            } />
            
            {/* Blog */}
            <Route path="/blog" element={
              <AuthGuard>
                <Layout>
                  <BlogManagement />
                </Layout>
              </AuthGuard>
            } />
            
            {/* Reservations */}
            <Route path="/reservations" element={
              <AuthGuard>
                <Layout>
                  <Reservations />
                </Layout>
              </AuthGuard>
            } />
            
            {/* Reviews */}
            <Route path="/reviews" element={
              <AuthGuard>
                <Layout>
                  <Reviews />
                </Layout>
              </AuthGuard>
            } />
            
            {/* Settings */}
            <Route path="/settings" element={
              <AuthGuard>
                <Layout>
                  <Settings />
                </Layout>
              </AuthGuard>
            } />
            
            {/* Unauthorized */}
            <Route path="/unauthorized" element={<Unauthorized />} />
            
            {/* Catch all */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
          </div>
        </Router>
        </MergeGroupProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
